import type { SpaBuyerCodes, SpaBuyerCodesRecord } from '@erp/rpc';
import type { SdTransactionStatus } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';

export interface SpaDunsComparison {
  /** The `N1*BY` code we submitted, echoed back on the 849 as `buyerIdCode`. */
  submitted: string | null;
  /** The line's invoice date (`YYYY-MM-DD`) the SPAs were judged against; null when the 849 has none. */
  asOf: string | null;
  /** Records active on {@link asOf} (all on file when it is unknown). */
  activeSpas: SpaBuyerCodesRecord[];
  /** Distinct codes across {@link activeSpas}. */
  spaCodes: string[];
  /** Records on file for the contract, active or not. */
  spasOnFile: SpaBuyerCodesRecord[];
  /** True only when the submitted code is one an active SPA names. */
  matches: boolean;
}

const rawText = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';

/** 844/849 dates are X12 `CCYYMMDD`; SPA windows are `YYYY-MM-DD`. */
function toIsoDate(ediDate: string): string | null {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(ediDate);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function isActiveOn(spa: SpaBuyerCodesRecord, date: string): boolean {
  return spa.startDate <= date && spa.endDate >= date;
}

/**
 * A DUNS beside the DUNS on every SPA for its contract that was active on the
 * claim's invoice date — Schneider judges eligibility as of the sale, and
 * states one branch per SPA record, so a contract's branches are spread across
 * several records. Null when no SPA naming a buyer is on file for the
 * contract. Codes compare verbatim — leading zeros are significant on these.
 */
export function compareDunsToSpas({
  duns,
  contractNumber,
  invoiceDate,
  spaBuyerCodes,
}: {
  /** The `N1*BY` code sent, or about to be sent, on the 844. */
  duns: unknown;
  contractNumber: unknown;
  /** X12 `CCYYMMDD`, as a string or number. */
  invoiceDate: unknown;
  spaBuyerCodes: readonly SpaBuyerCodes[] | undefined;
}): SpaDunsComparison | null {
  const contract = rawText(contractNumber);
  const onFile = contract
    ? spaBuyerCodes?.find((s) => s.contractNumber === contract)?.spas
    : undefined;
  if (!onFile || onFile.length === 0) return null;

  const asOf = toIsoDate(rawText(invoiceDate));
  const activeSpas =
    asOf === null ? onFile : onFile.filter((spa) => isActiveOn(spa, asOf));
  const spaCodes = [...new Set(activeSpas.flatMap((spa) => spa.codes))];
  const submitted = rawText(duns) || null;
  return {
    submitted,
    asOf,
    activeSpas,
    spaCodes,
    spasOnFile: onFile,
    matches: submitted !== null && spaCodes.includes(submitted),
  };
}

/**
 * {@link compareDunsToSpas} for an 849 rejection-code-1 line: the `buyerIdCode`
 * the 849 echoes is the DUNS we sent. The endpoint only looks SPAs up for
 * code-1 contracts; the code is re-checked here because a contract can also
 * appear on a line rejected for another reason.
 */
export function compareSpaDuns(
  rejectionCode: string | null,
  rawData: Record<string, unknown>,
  spaBuyerCodes: readonly SpaBuyerCodes[] | undefined,
): SpaDunsComparison | null {
  if (Number(rejectionCode ?? NaN) !== 1) return null;
  return compareDunsToSpas({
    duns: rawData.buyerIdCode,
    contractNumber: rawData.contractNumber,
    invoiceDate: rawData.invoiceDate,
    spaBuyerCodes,
  });
}

/**
 * The DUNS check on a disputed claim being edited for resubmission: the DUNS
 * it will be resent with beside the DUNS on the SPAs for its contract active
 * on its invoice date. Unlike {@link stagedDunsWarning} it is shown whether or
 * not they match, and while the DUNS is blank — the reviewer is choosing the
 * DUNS to resend, so the branches the SPAs name are the answer, not only a
 * warning.
 *
 * Never a match when the claim has no invoice date: every SPA on file is then
 * listed, expired ones included, so a code on one proves nothing.
 *
 * Null when the contract has been edited away from the one sent (only the
 * sent contract's SPAs are loaded) or no SPA naming a buyer is on file.
 */
export function resubmitDunsCheck({
  duns,
  contractNumber,
  sentContractNumber,
  invoiceDate,
  spaBuyerCodes,
}: {
  /** The DUNS / GL branch in the edit form. */
  duns: unknown;
  /** The contract in the edit form. */
  contractNumber: unknown;
  /** The contract on the 844 that was sent. */
  sentContractNumber: unknown;
  /** X12 `CCYYMMDD`, as a string or number. */
  invoiceDate: unknown;
  spaBuyerCodes: readonly SpaBuyerCodes[] | undefined;
}): SpaDunsComparison | null {
  if (rawText(contractNumber) !== rawText(sentContractNumber)) return null;
  const comparison = compareDunsToSpas({
    duns,
    contractNumber: sentContractNumber,
    invoiceDate,
    spaBuyerCodes,
  });
  if (!comparison) return null;
  return {
    ...comparison,
    matches: comparison.asOf !== null && comparison.matches,
  };
}

/**
 * What the resubmit editor's DUNS check says:
 * - `unjudged` — no invoice date, so SPAs on file are listed with no verdict;
 * - `noActiveSpa` — none of the contract's SPAs was active on that date;
 * - `match` / `mismatch` — the DUNS being resent is / is not on an active SPA;
 * - `blank` — the DUNS field is empty.
 */
export type ResubmitDunsVerdict =
  'unjudged' | 'noActiveSpa' | 'match' | 'mismatch' | 'blank';

export function resubmitDunsVerdict(
  comparison: SpaDunsComparison,
): ResubmitDunsVerdict {
  if (comparison.asOf === null) return 'unjudged';
  if (comparison.activeSpas.length === 0) return 'noActiveSpa';
  if (comparison.matches) return 'match';
  return comparison.submitted === null ? 'blank' : 'mismatch';
}

/**
 * The DUNS the resubmit editor fills in when it opens: the one DUNS the
 * active SPAs name, when the claim's DUNS is not it. Null when they name
 * several (a person picks one), when the claim already matches, or when there
 * is no invoice date or no active SPA to judge by.
 */
export function suggestedResubmitDuns(
  comparison: SpaDunsComparison | null,
): string | null {
  if (!comparison || !needsDuns(resubmitDunsVerdict(comparison))) return null;
  const [only, ...rest] = comparison.spaCodes;
  return only !== undefined && rest.length === 0 ? only : null;
}

/** The verdicts where the DUNS should be replaced with one an active SPA names. */
export function needsDuns(verdict: ResubmitDunsVerdict): boolean {
  return verdict === 'mismatch' || verdict === 'blank';
}

/**
 * Claims still on their way to the supplier — the pending and staged pages.
 * Once sent, the supplier has already decided; a "will reject" warning on a
 * sent claim (e.g. opened from an 849) would be wrong.
 */
const UNSENT_STATUSES: ReadonlySet<string> = new Set<SdTransactionStatus>([
  'pending',
  'staged',
]);

/** The claim fields the staged warning reads, as saved or as being edited. */
interface StagedClaimFields {
  duns: unknown;
  contractNumber: unknown;
}

/**
 * The warning a staged 844 earns: SPAs for its contract were active on its
 * invoice date and none names the DUNS it would be sent with — the supplier
 * will reject it with AAA code 1. The same rule as the list's "DUNS not on
 * SPA" filter (`buildDunsNotOnSpaClause`), so the two agree on every claim.
 *
 * Null — no warning — when the claim is no longer pending or staged (already
 * sent: the supplier has decided), no SPA was active (nothing to judge
 * against), the DUNS is blank (a separate validation rule's job), or the
 * claim has no invoice date (no day to judge "active" on).
 *
 * `edited` is the pricing form while it is open: the warning follows the DUNS
 * as it is typed. Only the saved contract's SPAs are loaded, so once the
 * contract itself is edited there is nothing to judge until it is saved.
 */
export function stagedDunsWarning({
  status,
  saved,
  invoiceDate,
  edited,
  spaBuyerCodes,
}: {
  /** The claim's `status` (`pending`, `staged`, `sent`, …). */
  status: unknown;
  saved: StagedClaimFields;
  /** X12 `CCYYMMDD`, as a string or number. */
  invoiceDate: unknown;
  edited: StagedClaimFields | null;
  spaBuyerCodes: readonly SpaBuyerCodes[] | undefined;
}): SpaDunsComparison | null {
  if (typeof status !== 'string' || !UNSENT_STATUSES.has(status)) return null;
  if (
    edited &&
    rawText(edited.contractNumber) !== rawText(saved.contractNumber)
  ) {
    return null;
  }
  const comparison = compareDunsToSpas({
    duns: (edited ?? saved).duns,
    contractNumber: saved.contractNumber,
    invoiceDate,
    spaBuyerCodes,
  });
  if (!comparison || comparison.submitted === null || comparison.asOf === null)
    return null;
  if (comparison.activeSpas.length === 0 || comparison.matches) return null;
  return comparison;
}
